import type { AppData } from '../types'

export const demoData: AppData = {
  evidenceCount: 46,
  predictionAccuracy: null,
  medianLeadTime: null,
  lastScan: new Date(Date.now() - 18 * 60 * 1000).toISOString(),
  nextScan: new Date(Date.now() + 5 * 60 * 60 * 1000).toISOString(),
  questions: [],
  hdmPosts: [],
  trends: [
    {
      id: 'demo-trend-1',
      name: 'Tiny dessert → giant-table reveal',
      trendClass: 'hybrid',
      geography: 'UAE',
      lifecycle: 'emerging',
      route: 'urgent',
      emergence: 77.6,
      relevance: 80,
      confidence: 0.89,
      independentCreators: 4,
      platforms: ['Instagram', 'TikTok'],
      reasons: [
        '4 independent creators after removing 1 likely repost',
        'Moved from a global creative account into UAE food creators',
        'Recent adoption rate is 2× the preceding observation window',
      ],
      uncertainty: ['One example appears to be a repost', 'Only one UAE carrier page has adopted it so far'],
      firstSeen: new Date(Date.now() - 30 * 60 * 60 * 1000).toISOString(),
      lastSeen: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
      evidenceCount: 5,
      sparkline: [2, 2, 3, 5, 8, 13],
    },
    {
      id: 'demo-trend-2',
      name: '“My Dubai era” place-confession format',
      trendClass: 'subject + language',
      geography: 'UAE',
      lifecycle: 'seed',
      route: 'watchlist',
      emergence: 52,
      relevance: 72,
      confidence: 0.57,
      independentCreators: 2,
      platforms: ['Instagram'],
      reasons: ['Semantically similar captions appeared across unrelated creators', 'Two different visual treatments share the same narrative'],
      uncertainty: ['Below the 3-creator urgent threshold', 'No cross-platform confirmation yet'],
      firstSeen: new Date(Date.now() - 10 * 60 * 60 * 1000).toISOString(),
      lastSeen: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      evidenceCount: 2,
      sparkline: [0, 0, 1, 1, 2, 2],
    },
    {
      id: 'demo-trend-3',
      name: 'Midnight Pulse remix',
      trendClass: 'audio',
      geography: 'GLOBAL',
      lifecycle: 'accelerating',
      route: 'watchlist',
      emergence: 68,
      relevance: 58,
      confidence: 0.75,
      independentCreators: 11,
      platforms: ['TikTok', 'Instagram'],
      reasons: ['Audio reuse accelerated globally', 'First two UAE Instagram uses appeared today'],
      uncertainty: ['No stable UAE visual convention has formed yet'],
      firstSeen: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
      lastSeen: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
      evidenceCount: 14,
      sparkline: [1, 2, 4, 7, 14, 25],
    },
  ],
  sources: [
    { id: 's1', username: 'hdmagency_ae', platform: 'Instagram', panel: 'HDM-owned', status: 'active', leadTimeHours: null, usefulDiscoveries: 0, repostRatio: 0, costUsd: 0 },
    { id: 's2', username: 'theuae', platform: 'Instagram', panel: 'Carrier', status: 'active', leadTimeHours: 7.4, usefulDiscoveries: 4, repostRatio: 0.28, costUsd: 0.04 },
    { id: 's3', username: 'lovindubai', platform: 'Instagram', panel: 'Mainstream', status: 'active', leadTimeHours: 18.2, usefulDiscoveries: 2, repostRatio: 0.46, costUsd: 0.04 },
    { id: 's4', username: 'suggested_visual_creator', platform: 'Instagram', panel: 'Early adopter', status: 'proposed', leadTimeHours: null, usefulDiscoveries: 0, repostRatio: 0, costUsd: 0 },
  ],
  health: [
    { name: 'memory', label: 'Permanent memory', status: 'ok', message: 'Database is reachable and writes are verified.', lastChecked: new Date().toISOString() },
    { name: 'apify', label: 'Instagram collection', status: 'warning', message: 'Waiting for a protected API connection.', action: 'Complete Guided Setup', lastChecked: new Date().toISOString() },
    { name: 'gemini', label: 'Public AI verification', status: 'warning', message: 'Waiting for a protected API connection.', action: 'Complete Guided Setup', lastChecked: new Date().toISOString() },
    { name: 'telegram', label: 'Telegram delivery', status: 'warning', message: 'Replacement token has not been connected.', action: 'Complete Guided Setup', lastChecked: new Date().toISOString() },
    { name: 'privacy', label: 'Private-data boundary', status: 'ok', message: 'Private HDM context is blocked from free AI.', lastChecked: new Date().toISOString() },
    { name: 'budget', label: 'Zero-cash guard', status: 'ok', message: 'Paid fallback is disabled.', lastChecked: new Date().toISOString() },
  ],
  beliefs: [
    { id: 'b1', claim: 'UAE cultural moments deserve additional attention for HDM.', confidence: 0.78, evidenceCount: 7, effect: '+8 relevance points when timing is still useful', status: 'proposed' },
    { id: 'b2', claim: 'A familiar visual mechanism should not make a new subject look novel.', confidence: 0.83, evidenceCount: 12, effect: 'Adds a repetition warning, not an automatic rejection', status: 'active' },
  ],
  reports: [
    { id: 'r1', type: 'daily', title: 'Daily digest — Demo', createdAt: new Date().toISOString(), summary: '1 urgent signal, 2 watchlist items, no collection failures.', trendCount: 3 },
  ],
  settings: {
    scanTimes: ['06:00', '13:00', '19:00'],
    digestTime: '09:00',
    sundayEmailTime: '10:00',
    insightsReminderTime: '20:00',
    timezone: 'Asia/Dubai',
    posture: 'balanced_low_noise',
    urgentDailyLimit: 2,
    urgentEmergenceMin: 70,
    urgentRelevanceMin: 60,
    urgentConfidenceMin: 0.65,
    watchEmergenceMin: 45,
    minimumIndependentCreators: 3,
    repeatOnlyOnLifecycleChange: true,
    quietHoursStart: '22:00',
    quietHoursEnd: '07:00',
    thirdPartyMediaDays: 30,
    acceptedTrendFollowupDays: 3,
    proposedSourcesRequireApproval: true,
    languages: ['English', 'Arabic', 'Persian'],
    geographies: ['UAE', 'GLOBAL'],
    emailRecipients: ['novin.jafarii@gmail.com'],
  },
}
